// 122 — Transfusión Sanguínea, Registro y Seguimiento (Fase 7, Tanda 6, ver PLAN.md). Transcripción campo por campo
// del PDF oficial del Anexo 1 MSP: hoja Registro idx 266 y hoja Seguimiento idx 267 (footer `SNS-MSP/HCU-form.122/2021`).
// Informe de brechas en `docs/hce-fase7/brechas-117-118-119-120-122.md`.
//
// Retirado de la UI por no estar impreso en el papel: lista de "producto / hemoderivado" (5 opciones inventadas),
// hora de inicio/fin sueltas y la toma única de signos vitales (peso y talla incluidos; sin TA). El papel trae
// una grilla de tomas con antes/durante/después. Los recursos ya guardados no se tocan.
//
// Decisiones ante vacíos del papel (recuento por conteo visual, no por texto): 11 filas en la tabla de componentes (contadas en la imagen)
// y 10 en la de seguimiento; "Inspección" y el resto de celdas van como texto libre sin inventar opciones; el
// "Total de componentes enviados" es un campo simple (el papel no dibuja celda clara); las firmas de terceros son
// manuscritas (se capturan nombre, fecha y hora). Temperatura, TA, FC y FR de la grilla antes/durante/después SÍ crean sus
// Observations (perfil vital-signs con LOINC; `tomasDe122` + `observacionesDeTomas`, #230) y además van como `Par[]` en el PDF firmado.
// Limitación del papel: la grilla trae una sola "hora de inicio" y una sola "hora finalizada" por fila, no una hora por toma. Por eso
// "antes" y "durante" se fechan con la hora de inicio y "después" con la hora finalizada (día siguiente si la hora final es menor que la
// de inicio, es decir, cruzó la medianoche); sin hora finalizada, "después" cae en la hora de inicio. No es la hora exacta de cada
// medición: el papel no la registra.
//
// FHIR (consumidores: `calidad-hc`/listados que leen `Procedure`): se conserva el `Procedure` `completed` con
// `basedOn` al `ServiceRequest` de 118, `code.text` legible y `note` con observaciones y reacción adversa.
import { contarFilasConDatos, duracionEntreHoras } from './calculosClinicosDatos';
import { conCodigoCie, paresDeCampos, valoresVacios, type CampoSimple, type Valores } from './camposSimplesDatos';
import { CAMPOS_SERVICIO_118 } from './bloques118Datos';
import type { DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import {
  estadoInicialTablaConColumnas,
  resumenFilaTablaConColumnas,
  type ColumnaTablaConColumnas,
  type EstadoTablaConColumnas,
  type FilaTablaConColumnas,
} from './uci/TablaConColumnas';
import { diaLocal, type TomaSignos } from './signosVitalesToma';
import type { Par } from './index';

const casilla = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'casilla' });
const filasNumeradas = (n: number): readonly FilaTablaConColumnas[] => Array.from({ length: n }, (_, i) => ({ clave: `f${i + 1}`, etiqueta: String(i + 1) }));

// ---------- REGISTRO · B. Servicio y prioridad ----------

/** Mismos campos de servicio/prioridad/alistar/tratamiento que 118-B (el papel los repite). */
export const CAMPOS_SERVICIO_122 = CAMPOS_SERVICIO_118;

export const CAMPOS_REACCIONES_PREVIAS_122: readonly CampoSimple[] = [
  { clave: 'reaccionesPrevias', etiqueta: 'Reacciones transfusionales adversas previas', tipo: 'siNo' },
  { clave: 'cuales', etiqueta: 'Cuáles' },
];

// ---------- REGISTRO · C. Componentes enviados y pruebas pretransfusionales ----------

export const FILAS_COMPONENTES_122 = filasNumeradas(11);
export const COLUMNAS_COMPONENTES_122: readonly ColumnaTablaConColumnas[] = [
  { clave: 'inspeccion', etiqueta: 'Inspección' },
  { clave: 'temperatura', etiqueta: 'T °C' },
  { clave: 'tipo', etiqueta: 'Tipo de componente sanguíneo' },
  { clave: 'grupo', etiqueta: 'Grupo ABO/Rh' },
  { clave: 'codigo', etiqueta: 'Código' },
  { clave: 'volumen', etiqueta: 'Volumen (cc)' },
  { clave: 'caducidad', etiqueta: 'Fecha de caducidad', tipo: 'fecha' },
];

export const CAMPOS_PRETRANSFUSIONALES_122: readonly CampoSimple[] = [
  { clave: 'grupoAboRh', etiqueta: 'Grupo ABO/Rh' },
  { clave: 'subgrupoAbo', etiqueta: 'Subgrupo ABO' },
  { clave: 'fenotipoRh', etiqueta: 'Fenotipo Rh' },
  { clave: 'coombsDirecto', etiqueta: 'Coombs directo' },
  { clave: 'pruebasCruzadas', etiqueta: 'Pruebas cruzadas' },
  { clave: 'investigacionAnticuerpos', etiqueta: 'Investigación de anticuerpos' },
  { clave: 'identificacionAnticuerpo', etiqueta: 'Identificación del anticuerpo' },
];

export const CAMPOS_TOTAL_OBSERVACIONES_122: readonly CampoSimple[] = [
  // Solo lectura: es la cantidad de filas de la tabla de componentes con tipo o código (ver `totalObservacionesEfectivo122`).
  { clave: 'total', etiqueta: 'Total de componentes sanguíneos enviados', tipo: 'numero', soloLectura: true },
  { clave: 'observaciones', etiqueta: 'Observaciones', tipo: 'area' },
];

/** Responsables del traslado: enviado / transportado / recibido por, cada uno con fecha y hora (la firma es manuscrita). */
const responsable = (clave: string, etiqueta: string): readonly CampoSimple[] => [
  { clave: `${clave}Nombre`, etiqueta: `${etiqueta} — nombre` },
  { clave: `${clave}Fecha`, etiqueta: `${etiqueta} — fecha`, tipo: 'fecha' },
  { clave: `${clave}Hora`, etiqueta: `${etiqueta} — hora`, tipo: 'hora' },
];
export const CAMPOS_TRASLADO_122: readonly CampoSimple[] = [...responsable('enviado', 'Enviado por'), ...responsable('transportado', 'Transportado por'), ...responsable('recibido', 'Recibido por')];

// ---------- SEGUIMIENTO · B. Seguimiento del proceso transfusional ----------

export const FILAS_SEGUIMIENTO_122 = filasNumeradas(10);
const tres = (clave: string, etiqueta: string): readonly ColumnaTablaConColumnas[] => [
  { clave: `${clave}Antes`, etiqueta: `${etiqueta} antes` },
  { clave: `${clave}Durante`, etiqueta: `${etiqueta} durante` },
  { clave: `${clave}Despues`, etiqueta: `${etiqueta} después` },
];
export const COLUMNAS_SEGUIMIENTO_122: readonly ColumnaTablaConColumnas[] = [
  { clave: 'horaInicio', etiqueta: 'Hora de inicio de transfusión' },
  { clave: 'codigo', etiqueta: 'Código del componente administrado' },
  { clave: 'via', etiqueta: 'Vía de administración' },
  { clave: 'catater', etiqueta: 'N° catéter utilizado' },
  ...tres('t', 'Temperatura °C'),
  ...tres('ta', 'Tensión arterial mmHg'),
  ...tres('fc', 'Frecuencia cardíaca/min'),
  ...tres('fr', 'Frecuencia respiratoria/min'),
  { clave: 'horaFin', etiqueta: 'Hora finalizada' },
  // Tiempo total = hora finalizada − hora de inicio (solo lectura; si la final es menor se entiende que pasó la medianoche).
  { clave: 'tiempoTotal', etiqueta: 'Tiempo total', calculada: (f) => duracionEntreHoras(f.horaInicio, f.horaFin) },
  { clave: 'volumen', etiqueta: 'Volumen administrado' },
  // Casilla Sí / No como en el papel (dos casilleros por fila), no texto libre.
  { clave: 'reaccion', etiqueta: 'Reacción adversa', tipo: 'siNo' },
  { clave: 'cual', etiqueta: 'Cuál' },
];

// ---------- SEGUIMIENTO · C. Notificación de Reacción Adversa Transfusional (RAT) ----------

export const CAMPOS_RAT_MOMENTO_122: readonly CampoSimple[] = [
  { clave: 'momento', etiqueta: 'Momento de la reacción', tipo: 'select', opciones: ['Durante la transfusión', 'Postransfusión'].map((e) => ({ valor: e, etiqueta: e })) },
  { clave: 'horaInicio', etiqueta: 'Hora de inicio de la reacción', tipo: 'hora' },
];

export const CAMPOS_RAT_SIGNOS_122: readonly CampoSimple[] = [
  ...['Anafilaxia', 'Cianosis', 'Disnea', 'Coagulación intravascular', 'Dolor lumbar', 'Edema pulmonar', 'Fiebre', 'Hemoglobinuria', 'Hemorragia', 'Hipertensión', 'Hipotensión', 'Ictericia', 'Náusea', 'Shock', 'Seroconversión', 'Taquicardia', 'Urticaria', 'Vómito'].map((s, i) => casilla(`signo${i}`, s)),
  casilla('signoOtro', 'Otro'),
  { clave: 'signoOtroEspecifique', etiqueta: 'Otro (especificar)' },
];

export const CAMPOS_RAT_IDENTIFICACION_122: readonly CampoSimple[] = [
  { clave: 'tiempo', etiqueta: 'Según tiempo', tipo: 'select', opciones: ['Inmediata (< 48 h)', 'Tardía (> 48 h)'].map((e) => ({ valor: e, etiqueta: e })) },
  ...[
    'Reacción febril no hemolítica',
    'Reacción hemolítica aguda',
    'Hemólisis no inmune',
    'Edema pulmonar no cardiogénico (TRALI)',
    'Reacción anafiláctica',
    'Reacción alérgica',
    'Sobrecarga circulatoria',
    'Sepsis',
    'Reacción hemolítica tardía',
    'Púrpura transfusional',
    'Enfermedad injerto contra huésped (EICH)',
    'Inmunomodulación',
    'Infección bacteriana',
    'Infección viral',
    'Infección parasitaria',
    'Sobrecarga de hierro',
  ].map((s, i) => casilla(`rat${i}`, s)),
  casilla('ratOtro', 'Otro'),
  { clave: 'ratOtroEspecifique', etiqueta: 'Otro (especificar)' },
];

// ---------- SEGUIMIENTO · D. Datos del profesional responsable ----------

const profesional = (clave: string, etiqueta: string): readonly CampoSimple[] => [
  { clave: `${clave}Nombre`, etiqueta: `${etiqueta} — nombre y apellido` },
  { clave: `${clave}Codigo`, etiqueta: `${etiqueta} — sello y código` },
];
export const CAMPOS_RESPONSABLES_122: readonly CampoSimple[] = [
  ...profesional('smt', 'Responsable del SMT (servicio de medicina transfusional)'),
  ...profesional('inicio', 'Responsable inicio transfusión'),
  ...profesional('fin', 'Responsable fin transfusión'),
];

/** Cantidad de componentes sanguíneos enviados: filas de la tabla con tipo de componente o código (`''` si no hay ninguna). */
export function totalComponentes122(componentes: EstadoTablaConColumnas): string {
  // Un componente es una fila con tipo o código: una fila con solo "T °C" o "Inspección" no cuenta como componente enviado.
  const n = contarFilasConDatos(componentes, FILAS_COMPONENTES_122.map((f) => f.clave), ['tipo', 'codigo']);
  return n === 0 ? '' : String(n);
}

/** `totalObservaciones` con el total de componentes calculado desde la tabla (lo que se ve y se imprime). */
export function totalObservacionesEfectivo122(e: Pick<Estado122, 'totalObservaciones' | 'componentes'>): Valores {
  return { ...e.totalObservaciones, total: totalComponentes122(e.componentes) };
}

export interface Estado122 {
  readonly diagnosticoPrincipal: DiagnosticoCie10Valor;
  readonly diagnosticoTransfusion: DiagnosticoCie10Valor;
  readonly servicio: Valores;
  readonly reaccionesPrevias: Valores;
  readonly componentes: EstadoTablaConColumnas;
  readonly pretransfusionales: Valores;
  readonly totalObservaciones: Valores;
  readonly traslado: Valores;
  readonly seguimiento: EstadoTablaConColumnas;
  readonly ratMomento: Valores;
  readonly ratSignos: Valores;
  readonly ratIdentificacion: Valores;
  readonly responsables: Valores;
}

export function estadoInicial122(): Estado122 {
  return {
    diagnosticoPrincipal: { texto: '', cie10: null },
    diagnosticoTransfusion: { texto: '', cie10: null },
    servicio: valoresVacios(CAMPOS_SERVICIO_122),
    reaccionesPrevias: valoresVacios(CAMPOS_REACCIONES_PREVIAS_122),
    componentes: estadoInicialTablaConColumnas(FILAS_COMPONENTES_122, COLUMNAS_COMPONENTES_122),
    pretransfusionales: valoresVacios(CAMPOS_PRETRANSFUSIONALES_122),
    totalObservaciones: valoresVacios(CAMPOS_TOTAL_OBSERVACIONES_122),
    traslado: valoresVacios(CAMPOS_TRASLADO_122),
    seguimiento: estadoInicialTablaConColumnas(FILAS_SEGUIMIENTO_122, COLUMNAS_SEGUIMIENTO_122),
    ratMomento: valoresVacios(CAMPOS_RAT_MOMENTO_122),
    ratSignos: valoresVacios(CAMPOS_RAT_SIGNOS_122),
    ratIdentificacion: valoresVacios(CAMPOS_RAT_IDENTIFICACION_122),
    responsables: valoresVacios(CAMPOS_RESPONSABLES_122),
  };
}

/** Tipos de componente cargados en la tabla de Registro-C, sin repetir. */
export function tiposComponentes(e: Estado122): string[] {
  return [...new Set(FILAS_COMPONENTES_122.map((f) => (e.componentes[f.clave]?.tipo ?? '').trim()).filter(Boolean))];
}

/** `Procedure.code.text`. */
export function textoProcedimiento122(e: Estado122): string {
  const tipos = tiposComponentes(e);
  return tipos.length ? `Transfusión sanguínea — ${tipos.join(', ')}` : 'Transfusión sanguínea';
}

/** Casillas RAT marcadas (etiqueta) + el "otro" escrito. */
export function ratMarcados(campos: readonly CampoSimple[], valores: Valores, otraClave: string, textoOtro: string): string[] {
  const marcados = campos.filter((c) => c.tipo === 'casilla' && c.clave !== otraClave && valores[c.clave] === 'true').map((c) => c.etiqueta);
  if (valores[otraClave] === 'true' || (valores[textoOtro] ?? '').trim()) marcados.push(`Otro${(valores[textoOtro] ?? '').trim() ? `: ${valores[textoOtro]!.trim()}` : ''}`);
  return marcados;
}

/** Resumen de la RAT para `Procedure.note` — vacío si no hay ninguna reacción anotada. */
export function resumenRat122(e: Estado122): string {
  const signos = ratMarcados(CAMPOS_RAT_SIGNOS_122, e.ratSignos, 'signoOtro', 'signoOtroEspecifique');
  const ident = ratMarcados(CAMPOS_RAT_IDENTIFICACION_122, e.ratIdentificacion, 'ratOtro', 'ratOtroEspecifique');
  const partes = [
    (e.ratMomento.momento ?? '').trim() ? `Momento: ${e.ratMomento.momento}` : '',
    signos.length ? `Signos y síntomas: ${signos.join(', ')}` : '',
    ident.length ? `Identificación: ${[(e.ratIdentificacion.tiempo ?? '').trim(), ...ident].filter(Boolean).join(', ')}` : '',
  ].filter(Boolean);
  return partes.length ? `Reacción adversa transfusional — ${partes.join('. ')}` : '';
}

const filasPares = (titulo: string, filas: readonly FilaTablaConColumnas[], columnas: readonly ColumnaTablaConColumnas[], estado: EstadoTablaConColumnas): Par[] =>
  filas.map((f) => ({ pregunta: `${titulo} — fila ${f.etiqueta}`, respuesta: resumenFilaTablaConColumnas(estado, f.clave, columnas) }));

const HORA_VALIDA = /^\d{2}:\d{2}$/;

/** aaaa-mm-dd del día local siguiente al instante `hoyIso`. */
function diaSiguiente(hoyIso: string): string {
  const d = new Date(hoyIso);
  d.setDate(d.getDate() + 1);
  return diaLocal(d.toISOString());
}

/**
 * Tomas del seguimiento (B): por cada fila, una toma "antes", "durante" y "después" con temperatura, TA, FC y FR. El papel no trae hora por
 * toma (ver la cabecera): "antes" y "durante" llevan la hora de inicio de la transfusión y "después" la hora finalizada (día siguiente si cruzó
 * la medianoche; hora de inicio si no hay hora finalizada). El momento viaja en la extensión `momento-transfusion` de cada Observation.
 */
export function tomasDe122(e: Estado122, hoyIso: string = new Date().toISOString()): TomaSignos[] {
  const momentos = [['Antes', 'antes'], ['Durante', 'durante'], ['Despues', 'después']] as const;
  return FILAS_SEGUIMIENTO_122.flatMap((f) => {
    const c = e.seguimiento[f.clave] ?? {};
    const inicio = c.horaInicio;
    const fin = c.horaFin;
    const finUtil = HORA_VALIDA.test(fin ?? '') && HORA_VALIDA.test(inicio ?? '') ? fin : undefined;
    const cruzaMedianoche = finUtil !== undefined && inicio !== undefined && finUtil < inicio;
    return momentos.map(([sufijo, momento]) => {
      const despues = sufijo === 'Despues' && finUtil !== undefined;
      return {
        hora: despues ? finUtil : inicio,
        fecha: despues && cruzaMedianoche ? diaSiguiente(hoyIso) : undefined,
        temperatura: c[`t${sufijo}`],
        presion: c[`ta${sufijo}`],
        fc: c[`fc${sufijo}`],
        fr: c[`fr${sufijo}`],
        momento,
      };
    });
  });
}

/** Encabezados de las dos hojas del papel (pie de cada página del formulario 122): separan Registro y Seguimiento en el PDF firmado. */
export const TITULO_REGISTRO_122 = 'TRANSFUSIÓN SANGUÍNEA - REGISTRO';
export const TITULO_SEGUIMIENTO_122 = 'TRANSFUSIÓN SANGUÍNEA - SEGUIMIENTO';

export function paresDe122(e: Estado122): Par[] {
  const dx = (titulo: string, d: DiagnosticoCie10Valor): Par => ({ pregunta: titulo, respuesta: conCodigoCie(d) });
  const conRespuesta = (pares: Par[]): Par[] => pares.filter((p) => p.respuesta.trim() !== '');
  const registro = conRespuesta([
    dx('Diagnóstico principal', e.diagnosticoPrincipal),
    ...paresDeCampos(CAMPOS_SERVICIO_122.slice(0, 4), e.servicio),
    dx('Diagnóstico para transfusión', e.diagnosticoTransfusion),
    ...paresDeCampos(CAMPOS_SERVICIO_122.slice(4), e.servicio),
    ...paresDeCampos(CAMPOS_REACCIONES_PREVIAS_122, e.reaccionesPrevias),
    ...filasPares('Componentes enviados', FILAS_COMPONENTES_122, COLUMNAS_COMPONENTES_122, e.componentes),
    ...paresDeCampos(CAMPOS_PRETRANSFUSIONALES_122, e.pretransfusionales, 'Pretransfusional — '),
    ...paresDeCampos(CAMPOS_TOTAL_OBSERVACIONES_122, totalObservacionesEfectivo122(e)),
    ...paresDeCampos(CAMPOS_TRASLADO_122, e.traslado),
  ]);
  const seguimiento = conRespuesta([
    ...filasPares('Seguimiento', FILAS_SEGUIMIENTO_122, COLUMNAS_SEGUIMIENTO_122, e.seguimiento),
    ...paresDeCampos(CAMPOS_RAT_MOMENTO_122, e.ratMomento, 'RAT — '),
    ...paresDeCampos(CAMPOS_RAT_SIGNOS_122, e.ratSignos, 'RAT signo — '),
    ...paresDeCampos(CAMPOS_RAT_IDENTIFICACION_122, e.ratIdentificacion, 'RAT identificación — '),
    ...paresDeCampos(CAMPOS_RESPONSABLES_122, e.responsables),
  ]);
  // Un encabezado solo si su hoja tiene datos; va con respuesta vacía y se imprime como línea de título.
  return [
    ...(registro.length ? [{ pregunta: TITULO_REGISTRO_122, respuesta: '' }, ...registro] : []),
    ...(seguimiento.length ? [{ pregunta: TITULO_SEGUIMIENTO_122, respuesta: '' }, ...seguimiento] : []),
  ];
}
